import { getBoolean, getNumber, getString } from './utils/env';

export type Environment = {
  readonly MODE: string;
  readonly RUNTIME_ENV: string;
  readonly APP_VERSION: string;
  readonly API_URL: string;
  readonly FULLSTORY_ORG_ID: string;
  readonly SENTRY_DSN: string;
  readonly DATADOG_RUM_APPLICATION_ID: string;
  readonly DATADOG_RUM_CLIENT_TOKEN: string;
  readonly DATADOG_RUM_ENV: string;
  readonly DATADOG_RUM_ENABLED: boolean;
  readonly LAUNCH_DARKLY_CLIENT_ID: string;
  readonly AUTH_IUM_ISSUER_URL: string;
  readonly AUTH_IUM_CLIENT_ID: string;
  readonly MONOLITH_URL: string;
  readonly SOFTPHONE_MFE_URL: string;
  readonly ENTERPRISE_HUB_URL: string;
  readonly BRAZE_API_KEY: string;
  readonly BRAZE_SDK_ENDPOINT: string;
  readonly HELP_CENTER_URL: string;
  readonly AIS_API_URL: string;
  readonly PENDO_API_KEY: string;
  readonly VOICE_INTELLIGENCE_API_URL: string;
  readonly VOICE_INTELLIGENCE_CALLS_MFE_URL: string;
  readonly VOICE_INTELLIGENCE_INSIGHTS_MFE_URL: string;
  readonly VOICE_INTELLIGENCE_SETTINGS_MFE_URL: string;
  readonly VOICE_INTELLIGENCE_COACHING_MFE_URL: string;
  readonly LAUNCH_DARKLY_PROXY_URL: string;
  readonly CONTACTCENTER_AGENT_INBOX_MFE_URL: string;
  readonly BOOKING_CONFIG_MFE_URL: string;
  readonly CUSTOMER_MFE_URL: string;
  readonly COMM_HUB_TEXT_LIST_URL: string;
  readonly COMM_HUB_TEXT_CONVERSATION_URL: string;
  readonly COMM_HUB_BFF_PREFIX: string;
  readonly CRM_BFF_PREFIX: string;
  readonly PARALLEL_MUTATIONS_MODAL_BATCH_SIZE: number;
  readonly PUSHER_APP_KEY: string;
  readonly PUSHER_CLUSTER: string;
};

/**
 * The following parses the environment variables and sets the default values
 * for the application.
 *
 * NOTE: The helper functions cannot be simplified down to only taking a string
 * because the vite preprocessor is looking for the import.meta.env.XXX to
 * determine the environment variables.
 */
const environment: Environment = {
  MODE: getString(import.meta.env.MODE, 'MODE'),
  RUNTIME_ENV: getString(import.meta.env.VITE_RUNTIME_ENV, 'VITE_RUNTIME_ENV'),
  APP_VERSION: getString(import.meta.env.VITE_APP_VERSION, 'VITE_APP_VERSION'),
  API_URL: getString(import.meta.env.VITE_API_URL, 'VITE_API_URL'),
  FULLSTORY_ORG_ID: getString(
    import.meta.env.VITE_FULLSTORY_ORG_ID,
    'VITE_FULLSTORY_ORG_ID',
  ),
  SENTRY_DSN: getString(import.meta.env.VITE_SENTRY_DSN, 'VITE_SENTRY_DSN'),
  DATADOG_RUM_APPLICATION_ID: getString(
    import.meta.env.VITE_DATADOG_RUM_APPLICATION_ID,
    'VITE_DATADOG_RUM_APPLICATION_ID',
    '',
  ),
  DATADOG_RUM_CLIENT_TOKEN: getString(
    import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN,
    'VITE_DATADOG_RUM_CLIENT_TOKEN',
    '',
  ),
  DATADOG_RUM_ENV: getString(
    import.meta.env.VITE_DATADOG_RUM_ENV,
    'VITE_DATADOG_RUM_ENV',
    'dev',
  ),
  DATADOG_RUM_ENABLED: getBoolean(
    import.meta.env.VITE_DATADOG_RUM_ENABLED,
    'VITE_DATADOG_RUM_ENABLED',
    false,
  ),
  LAUNCH_DARKLY_CLIENT_ID: getString(
    import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID,
    'VITE_LAUNCH_DARKLY_CLIENT_ID',
  ),
  AUTH_IUM_ISSUER_URL: getString(
    import.meta.env.VITE_AUTH_IUM_ISSUER_URL,
    'VITE_AUTH_IUM_ISSUER_URL',
  ),
  AUTH_IUM_CLIENT_ID: getString(
    import.meta.env.VITE_AUTH_IUM_CLIENT_ID,
    'VITE_AUTH_IUM_CLIENT_ID',
    'default-client-id',
  ),
  MONOLITH_URL: getString(
    import.meta.env.VITE_MONOLITH_URL,
    'VITE_MONOLITH_URL',
  ),
  SOFTPHONE_MFE_URL: getString(
    import.meta.env.VITE_SOFTPHONE_MFE_URL,
    'VITE_SOFTPHONE_MFE_URL',
  ),
  ENTERPRISE_HUB_URL: getString(
    import.meta.env.VITE_ENTERPRISE_HUB_URL,
    'VITE_ENTERPRISE_HUB_URL',
  ),
  BRAZE_API_KEY: getString(
    import.meta.env.VITE_BRAZE_API_KEY,
    'VITE_BRAZE_API_KEY',
  ),
  BRAZE_SDK_ENDPOINT: getString(
    import.meta.env.VITE_BRAZE_SDK_ENDPOINT,
    'VITE_BRAZE_SDK_ENDPOINT',
  ),
  PENDO_API_KEY: getString(
    import.meta.env.VITE_PENDO_API_KEY,
    'VITE_PENDO_API_KEY',
  ),
  HELP_CENTER_URL: getString(
    import.meta.env.VITE_HELP_CENTER_URL,
    'VITE_HELP_CENTER_URL',
  ),
  AIS_API_URL: getString(import.meta.env.VITE_AIS_API_URL, 'VITE_AIS_API_URL'),
  VOICE_INTELLIGENCE_API_URL: getString(
    import.meta.env.VITE_VOICE_INTELLIGENCE_API_URL,
    'VITE_VOICE_INTELLIGENCE_API_URL',
  ),
  VOICE_INTELLIGENCE_INSIGHTS_MFE_URL: getString(
    import.meta.env.VITE_VOICE_INTELLIGENCE_INSIGHTS_MFE_URL,
    'VITE_VOICE_INTELLIGENCE_INSIGHTS_MFE_URL',
  ),
  VOICE_INTELLIGENCE_CALLS_MFE_URL: getString(
    import.meta.env.VITE_VOICE_INTELLIGENCE_CALLS_MFE_URL,
    'VITE_VOICE_INTELLIGENCE_CALLS_MFE_URL',
  ),
  VOICE_INTELLIGENCE_SETTINGS_MFE_URL: getString(
    import.meta.env.VITE_VOICE_INTELLIGENCE_SETTINGS_MFE_URL,
    'VITE_VOICE_INTELLIGENCE_SETTINGS_MFE_URL',
  ),
  VOICE_INTELLIGENCE_COACHING_MFE_URL: getString(
    import.meta.env.VITE_VOICE_INTELLIGENCE_COACHING_MFE_URL,
    'VITE_VOICE_INTELLIGENCE_COACHING_MFE_URL',
  ),
  LAUNCH_DARKLY_PROXY_URL: getString(
    import.meta.env.VITE_LAUNCH_DARKLY_PROXY_URL,
    'VITE_LAUNCH_DARKLY_PROXY_URL',
    '',
  ),
  CONTACTCENTER_AGENT_INBOX_MFE_URL: getString(
    import.meta.env.VITE_CONTACTCENTER_AGENT_INBOX_MFE_URL,
    'VITE_CONTACTCENTER_AGENT_INBOX_MFE_URL',
  ),
  BOOKING_CONFIG_MFE_URL: getString(
    import.meta.env.VITE_BOOKING_CONFIG_MFE_URL,
    'VITE_BOOKING_CONFIG_MFE_URL',
  ),
  CUSTOMER_MFE_URL: getString(
    import.meta.env.VITE_CUSTOMER_MFE_URL,
    'VITE_CUSTOMER_MFE_URL',
  ),
  COMM_HUB_TEXT_LIST_URL: getString(
    import.meta.env.VITE_COMM_HUB_TEXT_LIST_URL,
    'VITE_COMM_HUB_TEXT_LIST_URL',
  ),
  COMM_HUB_TEXT_CONVERSATION_URL: getString(
    import.meta.env.VITE_COMM_HUB_TEXT_CONVERSATION_URL,
    'VITE_COMM_HUB_TEXT_CONVERSATION_URL',
  ),
  COMM_HUB_BFF_PREFIX: getString(
    import.meta.env.VITE_COMM_HUB_BFF_PREFIX,
    'VITE_COMM_HUB_BFF_PREFIX',
    'comm-hub',
  ),
  CRM_BFF_PREFIX: getString(
    import.meta.env.VITE_CRM_BFF_PREFIX,
    'VITE_CRM_BFF_PREFIX',
    '',
  ),
  PARALLEL_MUTATIONS_MODAL_BATCH_SIZE: getNumber(
    import.meta.env.VITE_PARALLEL_MUTATIONS_MODAL_BATCH_SIZE,
    'VITE_PARALLEL_MUTATIONS_MODAL_BATCH_SIZE',
  ),
  PUSHER_APP_KEY: getString(
    import.meta.env.VITE_PUSHER_APP_KEY,
    'VITE_PUSHER_APP_KEY',
    '',
  ),
  PUSHER_CLUSTER: getString(
    import.meta.env.VITE_PUSHER_CLUSTER,
    'VITE_PUSHER_CLUSTER',
    '',
  ),
};

export default environment;
